'use client'

import type { Editor } from '@tiptap/react'

import React from 'react'

export const hasEditorExtension = (editor: Editor, extensionName: string) =>
  editor.extensionManager.extensions.some(
    (extension) => extension.name === extensionName
  )

export const hasEditorCommand = (editor: Editor, commandName: string) =>
  typeof (editor.commands as Record<string, unknown>)[commandName] ===
  'function'

export interface ToolbarContextProps {
  editor: Editor
  hasCommand: (commandName: string) => boolean
  hasExtension: (extensionName: string) => boolean
}

export const ToolbarContext = React.createContext<null | ToolbarContextProps>(
  null
)

interface ToolbarProviderProps {
  children: React.ReactNode
  editor: Editor
}

export const ToolbarProvider = ({ children, editor }: ToolbarProviderProps) => {
  return (
    <ToolbarContext.Provider
      value={{
        editor,
        hasCommand: (commandName) => hasEditorCommand(editor, commandName),
        hasExtension: (extensionName) =>
          hasEditorExtension(editor, extensionName)
      }}
    >
      {children}
    </ToolbarContext.Provider>
  )
}

export const useToolbar = () => {
  const context = React.useContext(ToolbarContext)

  if (!context) {
    throw new Error('useToolbar must be used within a ToolbarProvider')
  }

  return context
}
